import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import { useForm, useWatch } from "react-hook-form";

import { SchemaFormData } from "src/schema/schemaEditor/types";

export const useSchemaForm = <T extends SchemaFormData>(
  schema: T | undefined,
  onSave: (data: T) => Promise<T>,
) => {
  const [isSaving, setIsSaving] = useState(false);
  const lastSavedSchema = useRef<T | undefined>(schema);
  const form = useForm<T>({
    values: schema,
  });

  const watchedValues = useWatch({ control: form.control }) as T;
  const isDirty = form.formState.isDirty;

  const getCleanedSchema = useCallback((data: T): T => {
    if (!data) return data;
    const cleaned = {
      ...data,
      properties: Object.fromEntries(
        Object.entries(data.properties || {}).map(([key, prop]) => {
          const { _sensitive, _masking, _maskingCache, ...rest } = prop;
          let newProp = { ...rest };
          if (_sensitive) {
            newProp = { ...newProp, _sensitive: true };
            if (_masking) {
              if (_masking._strategy === "partial" && _masking._options) {
                newProp = {
                  ...newProp,
                  _masking: {
                    ..._masking,
                    _options: {
                      ..._masking._options,
                      _keep_left: String(_masking._options._keep_left ?? "0"),
                      _keep_right: String(_masking._options._keep_right ?? "0"),
                      _mask_character: String(
                        _masking._options._mask_character ?? "*",
                      ),
                    },
                  },
                };
              } else {
                newProp = { ...newProp, _masking };
              }
            }
          }
          return [key, newProp];
        }),
      ),
    };
    return cleaned;
  }, []);

  useEffect(() => {
    if (schema) {
      lastSavedSchema.current = getCleanedSchema(schema);
    }
  }, [getCleanedSchema, schema]);

  const handleSubmit = async (data: T) => {
    try {
      setIsSaving(true);
      const cleanedData = getCleanedSchema(data);
      const result = await onSave(cleanedData);
      const cleanedResult = getCleanedSchema(result);
      lastSavedSchema.current = cleanedResult;
      form.reset(cleanedResult);
      return cleanedResult;
    } finally {
      setIsSaving(false);
    }
  };

  const canSave = useMemo(() => {
    if (isSaving) return false;
    if (!watchedValues || !schema) return false;
    if (!isDirty) return false;

    const lastSaved = lastSavedSchema.current;
    if (!lastSaved) return false;

    const currentStr = JSON.stringify(getCleanedSchema(watchedValues));
    const lastSavedStr = JSON.stringify(lastSaved);
    return currentStr !== lastSavedStr;
  }, [watchedValues, isSaving, schema, isDirty, getCleanedSchema]);

  return {
    form,
    handleSubmit,
    canSave,
    isSaving,
  };
};
